import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import addIcon from '@/assets/icons/add.svg';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import styles from './SharedStyles.module.css';

interface FileUploadAreaProps {
  onFileSelected: (file: File) => void;
  accept?: string;
  templateApiUrl?: string;
  templateFileName?: string;
}

export default function FileUploadArea({
  onFileSelected,
  accept = '.xlsx,.xls',
  templateApiUrl,
  templateFileName,
}: FileUploadAreaProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
  };

  // The workbook is generated per request so that its bank list is the current one, which means
  // it comes from an authenticated endpoint rather than a static file.
  const handleDownloadTemplate = async () => {
    if (!templateApiUrl || isDownloading) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadFileFromApi(templateApiUrl, templateFileName ?? 'template.xlsx', currentUser?.token ?? '');
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.uploadSection}>
      <div className="flex items-center justify-end gap-4 h-11 px-4 py-2 rounded-md bg-(--surface-grey-low-2) w-full max-w-[500px]">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
        <span className="body-md-regular flex-1 text-right text-(--font-body-3)">
          {t('bulkUploads.selectFile')}
        </span>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="h-7 px-2 rounded border border-dashed flex items-center gap-1.5 body-sm-semibold bg-(--surface-action) border-(--border-primary) text-(--font-primary) hover:bg-(--surface-primary-lowest) transition-colors"
        >
          {t('uploadFile')}
          <img src={addIcon} alt="" />
        </button>
      </div>

      {templateApiUrl && (
        <>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
            className={`${styles.templateLink} body-sm-semibold`}
          >
            {isDownloading ? t('bulkUploads.preparingTemplate') : t('bulkUploads.downloadTemplate')}
          </button>
          {downloadFailed && (
            <p className={`body-md-regular ${styles.errorText}`}>{t('bulkUploads.templateError')}</p>
          )}
        </>
      )}
    </div>
  );
}
